/**
 * Alta de un producto (ADM-02).
 *
 * El catalogo vive en la base del sitio, asi que este es el unico lugar donde
 * nace un producto. Nace OCULTO: recien creado no tiene foto ni descripcion, y se
 * publica desde su detalle cuando esta completo — por eso al guardar se salta
 * directo ahi.
 *
 * Tambien es como se agrega una talla o un color: el detalle de un producto
 * manda aca con sus datos ya escritos (`location.state.base`), y solo queda
 * cambiar el codigo y la opcion.
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import {
  FormularioProducto,
  type ValoresProducto,
} from '../../componentes/administracion/FormularioProducto';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { crearProducto, obtenerProductos } from '../../servicios/administracion.servicio';
import { obtenerCategoriasAdmin } from '../../servicios/portada.servicio';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { rutas } from '../../utilidades/rutas';

/** Lo que el detalle de un producto deja escrito para crear su siguiente variante. */
export type BaseDeVariante = Partial<ValoresProducto>;

/** Alcanza para juntar todos los grupos: el catalogo son decenas de productos. */
const PRODUCTOS_PARA_GRUPOS = 96;

export function ProductoNuevo() {
  const navegar = useNavigate();
  const base = (useLocation().state as { base?: BaseDeVariante } | null)?.base ?? {};

  const categorias = usePeticion((senal) => obtenerCategoriasAdmin(senal), []);
  const productos = usePeticion(
    (senal) => obtenerProductos({ porPagina: PRODUCTOS_PARA_GRUPOS }, senal),
    [],
  );

  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  async function guardar(valores: ValoresProducto): Promise<void> {
    setEnviando(true);
    setFalla(null);
    try {
      const creado = await crearProducto(valores);
      navegar(rutas.administracion.productoDetalle(creado.id));
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
      setEnviando(false);
    }
  }

  const grupos = [
    ...new Set(
      (productos.datos?.items ?? [])
        .map((producto) => producto.grupoVariante)
        .filter((grupo): grupo is string => grupo !== null),
    ),
  ];

  const primeraCategoria = categorias.datos?.[0];

  return (
    <>
      <Seo
        titulo="Nuevo producto"
        descripcion="Alta de un producto en el catalogo."
        ruta={rutas.administracion.productoNuevo()}
        sinIndexar
      />

      <BarraAdmin
        titulo={typeof base.grupoVariante === 'string' ? 'Nueva variante' : 'Nuevo producto'}
        bajada="Se crea oculto: agrégale fotos y descripción, y publícalo desde su detalle."
        acciones={
          <Link className="btn btn-sm btn-contorno" to={rutas.administracion.productos()}>
            <Icono nombre="flechaIzq" />
            Volver
          </Link>
        }
      />

      <div className="admin-cuerpo">
        {categorias.estado === 'cargando' ? <Cargando que="las categorias" /> : null}
        {categorias.estado === 'error' ? <AvisoError error={categorias.error} /> : null}

        {categorias.datos !== null && primeraCategoria !== undefined ? (
          <PanelAdmin titulo="Datos del producto">
            <FormularioProducto
              inicial={{
                sku: '',
                nombre: base.nombre ?? '',
                marca: base.marca ?? 'Bandu',
                categoriaId: base.categoriaId ?? primeraCategoria.id,
                precio: base.precio ?? 0,
                precioAnterior: base.precioAnterior ?? null,
                unidadMedida: base.unidadMedida ?? 'unidad',
                grupoVariante: base.grupoVariante ?? null,
                etiquetaVariante: null,
                ordenVariante: base.ordenVariante ?? 0,
                stockInicial: 0,
              }}
              categorias={categorias.datos}
              gruposExistentes={grupos}
              conStockInicial
              textoBoton="Crear producto"
              enviando={enviando}
              falla={falla}
              onGuardar={(valores) => {
                void guardar(valores);
              }}
            />
          </PanelAdmin>
        ) : null}
      </div>
    </>
  );
}
